import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { P, usePermissions } from '@/features/permissions';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

export function actionError(error: unknown, fallback: string): string {
  if (!isAxiosError(error)) return fallback;
  const status = error.response?.status;
  const detail = error.response?.data?.detail;
  if (status && status < 500 && typeof detail === 'string') return detail;
  if (status === 422 && Array.isArray(detail)) {
    return (
      'Check these fields: ' +
      detail
        .map((item: { loc?: string[] }) => item.loc?.filter((part) => part !== 'body').join(' →') || 'intake details')
        .join(', ')
    );
  }
  const requestId = error.response?.data?.request_id;
  return fallback + (typeof requestId === 'string' ? ` Reference: ${requestId}` : '');
}

export type Demographics = Record<string, string>;
export type Match = { patient_id: string | null; label?: string; matched_fields: string[]; blocked?: boolean };
const fields = [
  'first_name',
  'last_name',
  'date_of_birth',
  'gender',
  'phone',
  'email',
  'address',
  'insurance_provider',
  'insurance_policy_number',
  'insurance_group_number',
];

export function IntakeReview({
  jobId,
  matches,
  applied,
  referral,
  demographics,
  identityClear,
  assignedPatient,
  provisional,
}: {
  provisional?: {
    patient_id: string | null;
    status: string;
    patient_is_provisional?: boolean;
    patient_label?: string | null;
    version: number;
  } | null;
  jobId: string;
  demographics: Demographics;
  matches: Match[];
  referral: boolean;
  identityClear: boolean;
  applied?: string | null;
  assignedPatient?: { patient_id: string; label: string } | null;
}) {
  const { organization } = useAuth();
  const { can } = usePermissions();
  const cache = useQueryClient();
  const [values, setValues] = useState(demographics);
  const [currentMatches, setCurrentMatches] = useState(matches);
  const checkMatches = useMutation({
    onSuccess: setCurrentMatches,
    mutationFn: async () =>
      (
        await clientApi.post<Match[]>(
          `/api/v1/organizations/${organization?.organization_id}/agent/proposals/${jobId}/patient-matches`,
          values,
        )
      ).data,
  });
  const [existing, setExisting] = useState(provisional?.patient_id || '');
  const [correctionConfirmed, setCorrectionConfirmed] = useState(false);
  const assignmentConflict = !!assignedPatient && !!existing && assignedPatient.patient_id !== existing;
  const [draftOpen, setDraftOpen] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [received, setReceived] = useState(false);
  const [differentPatient, setDifferentPatient] = useState(false);
  const save = useMutation({
    onSuccess: () => {
      cache.invalidateQueries({ queryKey: ['patients'] });
      cache.invalidateQueries({ queryKey: ['maternal-labs'] });
      cache.invalidateQueries({ queryKey: ['patient'] });
      cache.invalidateQueries({ queryKey: ['agent-detail'] });
      cache.invalidateQueries({ queryKey: ['assistant-incoming'] });
    },
    mutationFn: async () =>
      (
        await clientApi.post<{ patient_id: string }>(
          `/api/v1/organizations/${organization?.organization_id}/agent/proposals/${jobId}/intake`,
          {
            reviewed,
            ...(provisional ? { expected_stage_version: provisional.version } : {}),
            demographics: values,
            referral_received: received,
            existing_patient_id: existing || null,
            different_patient_confirmed: differentPatient,
            reassign_from_patient_id: assignmentConflict && correctionConfirmed ? assignedPatient?.patient_id : null,
          },
        )
      ).data,
  });
  const [importedLabs, setImportedLabs] = useState(0);
  const syncLabs = useMutation({
    onSuccess: (data) => {
      setImportedLabs((count) => count + data.labs_imported);
      void cache.invalidateQueries({ queryKey: ['maternal-labs'] });
    },
    mutationFn: async (retryFailed: boolean) =>
      (
        await clientApi.post<{
          labs_imported: number;
          analysis_status: string;
          completed_pages: number;
          total_pages: number;
          failed_pages: number[];
        }>(`/api/v1/organizations/${organization?.organization_id}/agent/proposals/${jobId}/sync-labs`, {
          retry_failed: retryFailed,
        })
      ).data,
  });
  const labsRunning = ['extracting', 'queued'].includes(syncLabs.data?.analysis_status || '');
  const { mutate: refreshLabs } = syncLabs;
  useEffect(() => {
    if (!labsRunning || syncLabs.isPending || syncLabs.isError) return;
    const timer = window.setTimeout(() => refreshLabs(false), 5000);
    return () => window.clearTimeout(timer);
  }, [labsRunning, syncLabs.isPending, syncLabs.isError, syncLabs.data, refreshLabs]);
  const pid = applied || save.data?.patient_id;
  if (pid)
    return (
      <Alert>
        <AlertTitle>Intake saved</AlertTitle>
        <AlertDescription>
          {can(P.UPDATE_PATIENT) && can(P.UPDATE_DOCUMENT) && (
            <Button
              variant="outline"
              disabled={syncLabs.isPending || (labsRunning && !syncLabs.isError)}
              onClick={() => syncLabs.mutate(true)}>
              Recover / refresh laboratory results
            </Button>
          )}
          {syncLabs.data && (
            <p role="status">
              {importedLabs} new lab results imported. Existing results are preserved. {syncLabs.data.completed_pages}{' '}
              of {syncLabs.data.total_pages} pages complete.{' '}
              {labsRunning
                ? 'Remaining pages are processing from saved text. Results will refresh automatically while this entry is open.'
                : syncLabs.data.analysis_status === 'failed'
                  ? `Some pages still need review (${syncLabs.data.failed_pages.join(', ') || 'analysis incomplete'}). Results from completed pages are available in the chart. Recover / refresh retries only incomplete pages.`
                  : 'Laboratory analysis is complete.'}
            </p>
          )}
          {syncLabs.isError && (
            <p role="alert">{actionError(syncLabs.error, 'Labs could not be refreshed. Please retry.')}</p>
          )}
          <Link to="/patients/$patientId" params={{ patientId: pid }}>
            Open patient chart and Checklist
          </Link>
        </AlertDescription>
      </Alert>
    );
  if (provisional?.status === 'rejected')
    return <p>This provisional intake was rejected. No chart information was imported.</p>;
  if (!identityClear) return <p>Patient identity needs clarification. No patient will be created from this packet.</p>;
  if (!can(P.CREATE_PATIENT) || !can(P.UPDATE_PATIENT) || !can(P.UPDATE_DOCUMENT))
    return <p>Patient intake requires patient creation, editing, and document assignment permissions.</p>;
  return (
    <section className="flex flex-col gap-4" aria-label="Review patient intake">
      <h2>Review patient intake</h2>
      <p>
        Accept a patient match to link the document, add source history, fill missing demographics, and import
        recognized labs. Existing values are preserved; conflicts remain visible in the chart.
      </p>
      {provisional?.patient_id && (
        <p>Prepared for: {provisional.patient_label || 'Provisional chart'}. Confirm the identity before saving.</p>
      )}
      {provisional?.patient_is_provisional && existing === provisional.patient_id && (
        <details>
          <summary>Review or edit provisional demographics</summary>
          <FieldGroup>
            {fields.map((field) => (
              <Field key={field}>
                <FieldLabel htmlFor={`provisional-${field}`}>{field.replaceAll('_', ' ')}</FieldLabel>
                <Input
                  id={`provisional-${field}`}
                  value={values[field] || ''}
                  type={field === 'date_of_birth' ? 'date' : 'text'}
                  onChange={(e) => {
                    setValues({ ...values, [field]: e.target.value });
                    setReviewed(false);
                  }}
                />
              </Field>
            ))}
          </FieldGroup>
        </details>
      )}
      <section className="flex flex-col gap-3" aria-label="Patient match candidates">
        <h3>Patient match candidates</h3>
        {currentMatches.length === 0 && <p>No candidate found. Open Create a new draft patient below.</p>}
        {currentMatches.map((match, index) => (
          <div key={match.patient_id || index} className="flex flex-wrap items-center justify-between gap-2">
            <span>
              {match.label || 'Additional identity review required'} · matched {match.matched_fields.join(', ')}
            </span>
            {match.patient_id && !match.blocked && (
              <Button
                aria-pressed={existing === match.patient_id}
                variant={existing === match.patient_id ? 'secondary' : 'outline'}
                onClick={() => {
                  setExisting(match.patient_id!);
                  setCorrectionConfirmed(false);
                  setDraftOpen(false);
                  setReviewed(true);
                }}>
                {existing === match.patient_id ? 'Match accepted' : 'Accept match'}
              </Button>
            )}
          </div>
        ))}
        {existing && <p role="status">Match selected. Save intake below to update this patient’s chart.</p>}
      </section>
      {assignedPatient && <p>Currently assigned to: {assignedPatient.label}</p>}
      {assignmentConflict && (
        <Alert>
          <AlertTitle>Patient assignment conflict</AlertTitle>
          <AlertDescription>
            <p>
              This document is assigned to {assignedPatient!.label}. The selected match is a different patient record.
              Review the original document before changing its assignment.
            </p>
            <Field orientation="horizontal">
              <input
                id="assignment-correction"
                type="checkbox"
                checked={correctionConfirmed}
                onChange={(e) => setCorrectionConfirmed(e.target.checked)}
              />
              <FieldLabel htmlFor="assignment-correction">
                I verified the selected patient is correct and authorize correcting this document’s assignment
              </FieldLabel>
            </Field>
            <p>
              Existing chart data is not moved. If this document already contributed data to the other chart, the
              correction will require separate review.
            </p>
          </AlertDescription>
        </Alert>
      )}
      <Collapsible
        open={draftOpen}
        onOpenChange={(open) => {
          setDraftOpen(open);
          if (open) {
            setExisting('');
            setReviewed(false);
          }
        }}>
        <CollapsibleTrigger asChild>
          <Button variant="outline">Create a new draft patient</Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4">
          <FieldGroup>
            {fields.map((field) => (
              <Field key={field}>
                <FieldLabel htmlFor={`intake-${field}`}>{field.replaceAll('_', ' ')}</FieldLabel>
                <Input
                  id={`intake-${field}`}
                  value={values[field] || ''}
                  type={field === 'date_of_birth' ? 'date' : 'text'}
                  onChange={(e) => {
                    setValues({ ...values, [field]: e.target.value });
                    setReviewed(false);
                  }}
                />
              </Field>
            ))}
          </FieldGroup>
        </CollapsibleContent>
      </Collapsible>
      <FieldGroup>
        <Button variant="outline" disabled={checkMatches.isPending} onClick={() => checkMatches.mutate()}>
          Check patient matches
        </Button>
        {checkMatches.isError && (
          <p role="alert">{actionError(checkMatches.error, 'Patient matches could not be checked. Please retry.')}</p>
        )}
        {referral && (
          <Field orientation="horizontal">
            <input
              id="referral-received"
              type="checkbox"
              checked={received}
              onChange={(e) => {
                setReceived(e.target.checked);
              }}
            />
            <FieldLabel htmlFor="referral-received">
              I confirmed the referral request in the source; mark Referral received complete
            </FieldLabel>
          </Field>
        )}
        <Field orientation="horizontal">
          <input
            id="intake-reviewed"
            type="checkbox"
            checked={reviewed}
            onChange={(e) => setReviewed(e.target.checked)}
          />
          <FieldLabel htmlFor="intake-reviewed">I confirmed the patient identity for this document</FieldLabel>
        </Field>
      </FieldGroup>
      {!existing && currentMatches.length > 0 && (
        <Field orientation="horizontal">
          <input
            id="different-patient"
            type="checkbox"
            checked={differentPatient}
            onChange={(e) => {
              setDifferentPatient(e.target.checked);
              setReviewed(false);
            }}
          />
          <FieldLabel htmlFor="different-patient">
            I checked the suggested matches and confirmed this is a different patient (exact name/DOB or MRN conflicts
            still require resolution)
          </FieldLabel>
        </Field>
      )}
      {currentMatches.some((m) => m.blocked) && (
        <p role="alert">Additional identity review is required before creating a draft.</p>
      )}
      {save.isError && (
        <p role="alert">
          {actionError(
            save.error,
            'Intake was not saved because the server could not complete the request. Please retry.',
          )}
        </p>
      )}
      <Button
        onClick={() => save.mutate()}
        disabled={
          !reviewed ||
          (assignmentConflict && !correctionConfirmed) ||
          (!existing && !draftOpen) ||
          save.isPending ||
          (!existing && currentMatches.some((m) => m.blocked))
        }>
        {save.isPending
          ? 'Saving…'
          : existing
            ? 'Save intake to matched patient'
            : 'Create draft patient and checklist'}
      </Button>
    </section>
  );
}

export function ReprocessSource({ revisionId, enabled = true }: { revisionId: string; enabled?: boolean }) {
  const { organization } = useAuth();
  const { can } = usePermissions();
  const cache = useQueryClient();
  const retry = useMutation({
    onSuccess: () => cache.invalidateQueries({ queryKey: ['agent-jobs'] }),
    mutationFn: () =>
      clientApi.post(`/api/v1/organizations/${organization?.organization_id}/agent/trigger`, {
        revision_id: revisionId,
      }),
  });
  if (!can('trigger_agent')) return null;
  return (
    <div className="flex flex-col gap-2">
      <Button
        variant="outline"
        disabled={!enabled || retry.isPending || retry.isSuccess}
        onClick={() => retry.mutate()}>
        Reprocess saved text
      </Button>
      <p>
        {enabled
          ? 'Uses the existing extraction. A new processing version consumes one remaining pilot slot.'
          : 'Reprocessing is unavailable while agent processing is disabled or no pilot runs remain.'}
      </p>
      {retry.isSuccess && <p role="status">Submitted. Select the latest worklist entry when processing completes.</p>}
      {retry.isError && (
        <p role="alert">{actionError(retry.error, 'Could not submit for processing. Please retry.')}</p>
      )}
    </div>
  );
}
